import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Glass redesign — Phase 2 primitive.
 *
 * Translucent card: the canonical surface for kanban cards, list group
 * heads, calendar event chips, task-detail tiles, and the empty-state
 * frame. Wraps a `<div>` (or `<button>` when `interactive`) and applies:
 *
 *   - Translucent fill (`--glass-card-bg`) over a backdrop-blur
 *   - 1px translucent border that brightens on hover
 *   - Two-layer drop shadow that lifts on hover
 *   - Optional left-edge "status rail" — a 2px coloured strip at
 *     opacity 0.5 (per the user's "less shadow, thinner" feedback).
 *     Pass `railColor` as any CSS color (typically a `--status-*`
 *     token like `var(--status-in-progress)`).
 *
 * `interactive` swaps to a `<button>` so the consumer doesn't have
 * to manage focus / keyboard handling. Hover transforms (lift +
 * shadow + border) only apply in `interactive` mode.
 *
 * `density` toggles between cozy (default — prototype's stock
 * padding) and dense (tighter — used when the user opts into
 * the high-density list / board mode).
 */

type CardSize = 'cozy' | 'dense';

interface GlassCardCommonProps {
  railColor?: string;
  size?: CardSize;
  className?: string;
  children?: ReactNode;
}

type GlassCardDivProps = GlassCardCommonProps & {
  interactive?: false;
} & Omit<HTMLAttributes<HTMLDivElement>, 'className' | 'children'>;

type GlassCardButtonProps = GlassCardCommonProps & {
  interactive: true;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children'>;

export type GlassCardProps = GlassCardDivProps | GlassCardButtonProps;

const baseClasses =
  'relative rounded-xl border border-[var(--glass-card-border)] bg-[var(--glass-card-bg)] backdrop-blur-md transition-[transform,box-shadow,border-color] duration-150 ease-out';

const sizeClasses: Record<CardSize, string> = {
  cozy: 'p-3.5',
  dense: 'p-2.5',
};

const interactiveClasses =
  'cursor-pointer text-left hover:-translate-y-px hover:border-[var(--glass-card-hover-border)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-accent)]';

export const GlassCard = forwardRef<HTMLElement, GlassCardProps>((props, ref) => {
  const { railColor, size = 'cozy', className, children, ...rest } = props;
  const interactive = (rest as { interactive?: boolean }).interactive === true;

  // Combined inline style: shadow + (optional) status rail. Doing the
  // rail with a `box-shadow: inset Xpx 0 0 color` rather than a child
  // node keeps the DOM clean and the stripe edge-to-edge through the
  // rounded corner without needing an `overflow: hidden`.
  const style: Record<string, string> = {
    boxShadow: 'var(--glass-card-shadow)',
  };
  if (railColor) {
    style.boxShadow = `inset 2px 0 0 ${railColor}, var(--glass-card-shadow)`;
  }

  const railPaddingLeft = railColor ? 'pl-4' : '';
  const cls = cn(
    baseClasses,
    sizeClasses[size],
    railPaddingLeft,
    interactive && interactiveClasses,
    interactive && 'hover:[box-shadow:var(--glass-card-hover-shadow)]',
    className,
  );

  if (interactive) {
    const { interactive: _ignored, ...buttonRest } =
      rest as ButtonHTMLAttributes<HTMLButtonElement> & { interactive?: boolean };
    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type="button"
        className={cls}
        style={style}
        {...buttonRest}
      >
        {children}
      </button>
    );
  }

  return (
    <div
      ref={ref as React.Ref<HTMLDivElement>}
      className={cls}
      style={style}
      {...(rest as HTMLAttributes<HTMLDivElement>)}
    >
      {children}
    </div>
  );
});

GlassCard.displayName = 'GlassCard';
